import CloseIcon from "@material-ui/icons/Close";
import Navbar from "components/Navbar";

import React, { useState } from "react";

import { ApiErrors } from "common/api-errors/errors";
import { CommonButton, CommonCheckbox } from "common/components";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { ContentWrapper } from "components/ContentWrapper";
import { Formik, Form } from "formik";
import { Investment } from "common/store/investment/types";
import { RevokeInvestment } from "common/graphql/queryDescriptors";
import { StoreState } from "common/store/types";
import { TransactionTable } from "./components/TransactionTable";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  Box,
  Grid,
  Typography,
  Card,
  Button,
  withWidth,
  isWidthUp,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
} from "@material-ui/core";

import {
  showFile,
  ShowFile,
  DocumentType,
} from "services/fake/fakeDocumentManager";

import "./Dashboard.scss";

const RevocationModal = ({ open, setOpen, investmentID }: any) => {
  //State
  const [error, setError] = useState("");

  //Hooks
  const { t } = useTranslation();
  const { execute: revokeInvestment, loading: loadingRevoke } = useCustomQuery(
    RevokeInvestment({
      onError: (error: ApiErrors) => {
        switch (error) {
          case ApiErrors.INVEST_TOO_OLD:
            setError(t("Investment is too old to be revoked"));
            break;
          case ApiErrors.INVEST_NOT_FOUND:
            setError(t("Cannot revoke unknown investment"));
            break;
          default:
            break;
        }
      },
      onSuccess: () => {
        setOpen(false);
      },
    })
  );

  //Options
  const initialValues = {
    termsRevocation: false,
  };

  //Validation
  const onValidate = (values: any) => {
    let errors: any = {};

    if (!values.termsRevocation) {
      errors.termsRevocation = "Required";
    }

    return errors;
  };

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    setSubmitting(true);

    await revokeInvestment({
      variables: {
        id: investmentID,
      },
    });

    setSubmitting(false);
  };

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle>{t("Revoke Investment")}</DialogTitle>

        <IconButton
          className="close-icon--dialog"
          aria-label="close"
          onClick={() => setOpen(false)}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Formik
            initialValues={initialValues}
            validate={onValidate}
            onSubmit={onSubmit}
          >
            {({
              touched,
              errors,
              submitForm,
              isSubmitting,
              setFieldValue,
              values,
            }) => (
              <Form>
                <Grid container spacing={1}>
                  <Grid container item>
                    <Grid item xs={12} md={8}>
                      <CommonCheckbox
                        id="termsRevocation"
                        values={values}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        label={t("I agree to the revocation T&C")}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Button
                        className="primary--medium"
                        onClick={() =>
                          showFile(ShowFile.Open, DocumentType.Revocation)
                        }
                        style={{}}
                        fullWidth
                        variant="contained"
                      >
                        {t("Read")}
                      </Button>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    {loadingRevoke || isSubmitting ? (
                      <CircularProgress />
                    ) : (
                      <CommonButton
                        onClick={submitForm}
                        isSubmitting={isSubmitting}
                        values={values}
                        errors={errors}
                        touched={touched}
                        text={t("Revoke")}
                      />
                    )}
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <CommonErrorDialog error={error} setError={setError} />
    </React.Fragment>
  );
};

const DashboardCard = ({
  title,
  subtitle,
  amount,
  showExchangeButton,
  showRevokeButton,
  isFixed,
  grayed,
  investmentID,
}: any) => {
  //State
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);

  //Hooks
  const { t } = useTranslation();

  return (
    <Card
      className={"card" + (grayed ? "--grayed" : "")}
      style={
        isFixed
          ? {
              width: "362px",
            }
          : {}
      }
    >
      {title && (
        <Typography className={"title" + (grayed ? " line-through" : "")}>
          {title}
        </Typography>
      )}

      <Typography className={"subtitle" + (grayed ? " line-through" : "")}>
        {subtitle}
      </Typography>

      <Grid container direction={isFixed ? "row" : "column"}>
        <Grid item xs={7}>
          <Typography
            variant="subtitle2"
            className={"amount" + (grayed ? " line-through" : "")}
          >
            € {amount}
          </Typography>
        </Grid>

        <Grid item xs={5} container direction="column" spacing={3}>
          {showExchangeButton && (
            <Grid item className="grid-container">
              <Button className="secondary--small">{t("Exchange")}</Button>
            </Grid>
          )}

          {showRevokeButton && (
            <Grid item className="grid-container">
              <Button
                className="secondary--small"
                onClick={() => setRevokeModalOpen(true)}
              >
                {t("Revoke")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>

      <RevocationModal
        open={revokeModalOpen}
        setOpen={setRevokeModalOpen}
        investmentID={investmentID}
      />
    </Card>
  );
};

function Dashboard({ width }: any) {
  //Hooks
  const { t } = useTranslation();
  const investments = useSelector<StoreState, Investment[]>(
    (state) => state.currentUser.investments
  );

  //Helpers
  const getTotalAssets = () => {
    let total = 0;
    investments.map((investment: Investment) => (total += investment.paid));
    return total;
  };

  return (
    <Box>
      <Navbar />

      <ContentWrapper>
        <Box className="dashboard">
          <Grid item className="section">
            <Typography className="section-title">
              {t("Total assets")}
            </Typography>

            <DashboardCard
              subtitle={t("Total value")}
              amount={
                investments && investments.length > 0 ? getTotalAssets() : 0
              }
              isFixed={isWidthUp("sm", width)}
            />
          </Grid>

          {investments && investments.length > 0 && (
            <Grid item className="section">
              <Typography className="section-title">
                {t("My Investments")}
              </Typography>

              <Grid container spacing={isWidthUp("lg", width) ? 9 : 3}>
                {investments.map((investment: Investment, i: number) => (
                  <Grid item key={i}>
                    <DashboardCard
                      title={t("Sale #") + investment.sale_id}
                      investmentID={investment.id}
                      amount={investment.paid}
                      grayed={investment.status === "revoked"}
                      showExchangeButton={investment.status !== "revoked"}
                      showRevokeButton={false && investment.isRevocable()} //TODO(Marko): Revoke button temporary hidden
                      isFixed={isWidthUp("sm", width)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          <Grid item className="section">
            <Typography className="section-title">
              {t("Transactions")}
            </Typography>

            <Card className="card">
              <Typography className="title" variant="body1">
                {t("Recent Transactions")}
              </Typography>

              <TransactionTable />
            </Card>
          </Grid>
        </Box>
      </ContentWrapper>
    </Box>
  );
}

export default withWidth()(Dashboard);
